import { useEffect, useRef, useState, type FormEvent } from 'react';
import styled, { css } from 'styled-components';
import { Modal } from '@shared/ui/Modal';
import { Button } from '@shared/ui/Button';
import { Badge } from '@shared/ui/Badge';
import { ACCEPT_LEGAL_DOC } from '@shared/lib/fileAccept';
import { FileDropZone } from '@shared/ui/FileDropZone';
import { MultiFileList } from '@shared/ui/MultiFileList';
import { Table } from '@shared/ui/Table';
import { Input, Select, Label, Field } from '@shared/ui/Input';
import { Loader } from '@shared/ui/Progress';
import {
  IconChevronDown,
  IconChevronRight,
  IconDownload,
  IconFileStack,
  IconTrash,
} from '@shared/ui/icons';
import { downloadAuthorizedFile } from '@shared/lib/downloadFile';
import { useAppSelector } from '@app/hooks';
import {
  useDeleteCounterpartyDocumentMutation,
  useGetCounterpartyDocumentsQuery,
  useUploadCounterpartyDocumentsMutation,
} from '@shared/api';
import { useAgentPermission } from '@entities/user';
import {
  COUNTERPARTY_DOC_TYPE_LABELS,
  COUNTERPARTY_DOC_TYPE_ORDER,
  counterpartyDocTypeLabel,
  type CounterpartyDocType,
} from '@entities/legal/types';

/**
 * Блок «Документы контрагента (N)» в карточке контрагента. Репозиторий
 * уставных документов: устав и его редакции, приказы, доверенности (со сроком
 * действия), карточка предприятия, документы юр. адреса. В отличие от
 * документов претензии это НЕ вход для LLM-анализа — просто хранилище, поэтому
 * `content_chars = 0` (скан без текстового слоя) показываем нейтрально, без
 * тревожного «LLM не видела». Свёрнут по умолчанию; авто-разворот при ≤5
 * записях, чтобы юрист сразу увидел кнопку «Скачать».
 */

const Section = styled.div`
  margin-top: ${({ theme }) => theme.spacing(5)};
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${({ theme }) => theme.spacing(2)};
  margin-bottom: ${({ theme }) => theme.spacing(2)};
  flex-wrap: wrap;
`;

const Empty = styled.div`
  font-size: 13px;
  color: ${({ theme }) => theme.colors.text.muted};
  font-style: italic;
`;

const ToggleBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 0;
  background: transparent;
  border: none;
  cursor: pointer;
  font: inherit;
  color: ${({ theme }) => theme.colors.text.secondary};
  text-align: left;
  &:hover {
    color: ${({ theme }) => theme.colors.text.primary};
  }
  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.primary};
    outline-offset: 2px;
    border-radius: 2px;
  }
  & > h4 {
    margin: 0;
    font-size: 13px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.06em;
    color: inherit;
  }
`;

/** Горизонтальный скролл таблицы на узких экранах (360px): 6 колонок не
 *  влезают, но страница не должна ехать горизонтально — скроллит контейнер. */
const TableScroll = styled.div`
  overflow-x: auto;
`;

const FileRow = styled.tr<{ $highlight?: boolean }>`
  cursor: pointer;
  transition: background ${({ theme }) => theme.motion.fast}
    ${({ theme }) => theme.motion.easeOut};

  &:hover {
    background: ${({ theme }) => theme.colors.surface};
  }
  /* Видимое кольцо + подсветка фона: строка целиком кликабельна, поэтому
     фокус-индикатор не должен ограничиваться сменой фона (правило доступности).
     outline-offset отрицательный — кольцо внутри строки, не перекрывается
     соседними ячейками при border-collapse. */
  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.primary};
    outline-offset: -2px;
    background: ${({ theme }) => theme.colors.primarySoft};
  }
  /* Подсветка строки-источника при переходе из секции «Сверка реквизитов».
     Затухание через тот же transition (background) — оно снимается под
     prefers-reduced-motion глобально из GlobalStyle, отдельного правила не
     требуется. */
  ${({ $highlight, theme }) =>
    $highlight &&
    css`
      background: ${theme.colors.primarySoft};
      box-shadow: inset 3px 0 0 ${theme.colors.primary};
    `}
`;

const TypeCell = styled.td`
  white-space: nowrap;
`;

/** Нейтральная колонка «Текст»: репозиторий документов, а не вход LLM —
 *  скан без текстового слоя это норма, не ошибка, поэтому muted, без алерта. */
const MutedCell = styled.td`
  color: ${({ theme }) => theme.colors.text.muted};
`;

const ValidHint = styled.span`
  display: block;
  margin-top: 3px;
  font-size: 11px;
  color: ${({ theme }) => theme.colors.text.muted};
`;

const ValidExpired = styled.span`
  display: block;
  margin-top: 3px;
  font-size: 11px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.status.danger};
`;

const Uploader = styled.span`
  display: block;
  margin-top: 3px;
  font-size: 11px;
  color: ${({ theme }) => theme.colors.text.muted};
  max-width: 180px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const RowActions = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  justify-content: flex-end;
`;

const DownloadBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 10px;
  background: transparent;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.sm};
  cursor: pointer;
  font: inherit;
  font-size: 12px;
  color: ${({ theme }) => theme.colors.text.primary};
  transition: background ${({ theme }) => theme.motion.fast}
    ${({ theme }) => theme.motion.easeOut};
  &:hover {
    background: ${({ theme }) => theme.colors.primarySoft};
    border-color: ${({ theme }) => theme.colors.primary};
    color: ${({ theme }) => theme.colors.primary};
  }
  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.primary};
    outline-offset: 2px;
  }
`;

const DeleteBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 10px;
  background: transparent;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.sm};
  cursor: pointer;
  font: inherit;
  font-size: 12px;
  color: ${({ theme }) => theme.colors.status.danger};
  transition: background ${({ theme }) => theme.motion.fast}
    ${({ theme }) => theme.motion.easeOut};
  &:hover:not(:disabled) {
    background: ${({ theme }) => theme.colors.status.danger}14;
    border-color: ${({ theme }) => theme.colors.status.danger};
  }
  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.status.danger};
    outline-offset: 2px;
  }
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const SuccessBanner = styled.div`
  margin-top: ${({ theme }) => theme.spacing(3)};
  padding: ${({ theme }) => theme.spacing(2.5)} ${({ theme }) => theme.spacing(3)};
  background: ${({ theme }) => theme.colors.status.success}14;
  border-left: 3px solid ${({ theme }) => theme.colors.status.success};
  border-radius: ${({ theme }) => theme.radius.sm};
  color: ${({ theme }) => theme.colors.status.success};
  font-size: 13px;
  line-height: 1.5;
`;

const ErrorBanner = styled.div`
  margin-top: ${({ theme }) => theme.spacing(3)};
  padding: ${({ theme }) => theme.spacing(2.5)} ${({ theme }) => theme.spacing(3)};
  background: ${({ theme }) => theme.colors.status.danger}14;
  border-left: 3px solid ${({ theme }) => theme.colors.status.danger};
  border-radius: ${({ theme }) => theme.radius.sm};
  color: ${({ theme }) => theme.colors.status.danger};
  font-size: 13px;
  line-height: 1.5;
`;

const FieldHint = styled.p`
  margin: ${({ theme }) => theme.spacing(1.5)} 0 0;
  font-size: 12px;
  color: ${({ theme }) => theme.colors.text.muted};
  line-height: 1.45;
`;

const ModalActions = styled.div`
  margin-top: ${({ theme }) => theme.spacing(5)};
  display: flex;
  gap: ${({ theme }) => theme.spacing(2)};
  justify-content: flex-end;
  flex-wrap: wrap;
`;

function humanSize(bytes: number): string {
  if (!Number.isFinite(bytes) || bytes <= 0) return '—';
  const units = ['Б', 'КБ', 'МБ', 'ГБ'];
  let idx = 0;
  let val = bytes;
  while (val >= 1024 && idx < units.length - 1) {
    val /= 1024;
    idx += 1;
  }
  const formatted = idx >= 2 ? val.toFixed(2) : Math.round(val).toString();
  return `${formatted.replace('.', ',')} ${units[idx]}`;
}

function formatDateTime(value: string): string {
  return new Date(value).toLocaleString('ru-RU', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function formatDate(value: string): string {
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? value : d.toLocaleDateString('ru-RU');
}

/** Доверенность просрочена, если срок действия строго раньше сегодняшнего дня. */
function isExpired(validUntil: string): boolean {
  const d = new Date(validUntil);
  if (Number.isNaN(d.getTime())) return false;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return d.getTime() < today.getTime();
}

interface Props {
  counterpartyId: string;
  /** id документа, к которому нужно проскроллить и подсветить его — приходит
   *  при клике на «Документ-источник» в секции «Сверка реквизитов». Секция
   *  при этом разворачивается, если была свёрнута. */
  focusDocId?: string | null;
}

export function CounterpartyDocumentsSection({ counterpartyId, focusDocId }: Props) {
  const { can_write } = useAgentPermission('statut');
  const accessToken = useAppSelector((s) => s.auth.accessToken);
  const { data, isLoading } = useGetCounterpartyDocumentsQuery(counterpartyId);
  const [uploadDocs, { isLoading: isUploading }] =
    useUploadCounterpartyDocumentsMutation();
  const [deleteDoc, { isLoading: isDeleting }] =
    useDeleteCounterpartyDocumentMutation();

  const [modalOpen, setModalOpen] = useState(false);
  const [picked, setPicked] = useState<File[]>([]);
  const [docType, setDocType] = useState<CounterpartyDocType>('charter');
  const [validUntil, setValidUntil] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [expanded, setExpanded] = useState(false);
  const [autoApplied, setAutoApplied] = useState(false);
  const [highlightId, setHighlightId] = useState<string | null>(null);
  // Какой focusDocId уже обработан — чтобы фоновый рефетч списка (после
  // загрузки/удаления документа) не проскроллил повторно к старой строке.
  const handledFocusRef = useRef<string | null>(null);

  const items = data?.items ?? [];
  // Срок действия предлагаем указывать только у доверенностей — у устава и
  // приказов его нет.
  const showValidUntil = docType === 'power_of_attorney';

  useEffect(() => {
    if (!autoApplied && items.length > 0 && items.length <= 5) {
      setExpanded(true);
      setAutoApplied(true);
    }
  }, [autoApplied, items.length]);

  // Переход к документу-источнику из секции «Сверка реквизитов»: разворачиваем
  // список, скроллим к строке и подсвечиваем её на пару секунд. Зависим от
  // `data` (стабильная ссылка кеша RTK), а не от `items` (новый массив каждый
  // рендер) — иначе эффект дёргался бы на любом ре-рендере.
  useEffect(() => {
    if (!focusDocId) return;
    if (handledFocusRef.current === focusDocId) return;
    const exists = (data?.items ?? []).some((f) => f.id === focusDocId);
    if (!exists) return;
    handledFocusRef.current = focusDocId;
    setExpanded(true);
    setHighlightId(focusDocId);
    const raf = requestAnimationFrame(() => {
      document
        .getElementById(`cp-doc-row-${focusDocId}`)
        ?.scrollIntoView({ block: 'center' });
    });
    const timer = window.setTimeout(() => setHighlightId(null), 2500);
    return () => {
      cancelAnimationFrame(raf);
      window.clearTimeout(timer);
    };
  }, [focusDocId, data]);

  async function onDownload(docId: string, filename: string) {
    const base = import.meta.env.VITE_API_BASE_URL ?? '/api/v1';
    const res = await downloadAuthorizedFile(
      `${base}/agents/statut/counterparties/documents/${docId}`,
      { accessToken, fallbackName: filename },
    );
    if (!res.ok) setError(`Не удалось скачать файл (${res.error}).`);
  }

  async function onDelete(docId: string, filename: string) {
    if (!window.confirm(`Удалить документ «${filename}»? Действие необратимо.`)) {
      return;
    }
    setError(null);
    try {
      await deleteDoc({ counterpartyId, docId }).unwrap();
    } catch (err) {
      // RTK Query отдаёт ошибку как unknown (FetchBaseQueryError | Serialized);
      // сужаем к возможным формам тела, все ветки optional-chained — безопасно.
      const msg =
        (err as { data?: { error?: { message?: string } } })?.data?.error?.message ??
        (err as { error?: string })?.error ??
        'Не удалось удалить файл. Попробуйте ещё раз.';
      setError(msg);
    }
  }

  function onCloseModal() {
    setModalOpen(false);
    setPicked([]);
    setError(null);
  }

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (picked.length === 0) return;
    setError(null);
    const count = picked.length;
    const form = new FormData();
    for (const f of picked) {
      form.append('file', f);
    }
    form.append('doc_type', docType);
    // valid_until отправляем только для доверенностей и только если заполнено.
    if (showValidUntil && validUntil) {
      form.append('valid_until', validUntil);
    }
    try {
      await uploadDocs({ counterpartyId, form }).unwrap();
      setSuccess(
        `Добавлено документов: ${count} (${COUNTERPARTY_DOC_TYPE_LABELS[docType]}).`,
      );
      setValidUntil('');
      onCloseModal();
    } catch (err) {
      // См. onDelete: сужение unknown-ошибки RTK Query к формам тела ответа.
      const msg =
        (err as { data?: { error?: { message?: string } } })?.data?.error?.message ??
        (err as { error?: string })?.error ??
        'Не удалось загрузить документы. Попробуйте ещё раз.';
      setError(msg);
    }
  }

  return (
    <Section>
      <Header>
        <ToggleBtn
          type="button"
          onClick={() => setExpanded((v) => !v)}
          aria-expanded={expanded}
          aria-controls={`cp-docs-${counterpartyId}`}
          title={expanded ? 'Свернуть список документов' : 'Развернуть список документов'}
        >
          {expanded ? <IconChevronDown size={14} /> : <IconChevronRight size={14} />}
          <h4>Документы контрагента ({items.length})</h4>
        </ToggleBtn>
        <Button
          type="button"
          $variant="primary"
          onClick={() => {
            setSuccess(null);
            setModalOpen(true);
          }}
          disabled={!can_write}
          title={
            !can_write
              ? 'Только чтение'
              : 'Загрузить устав, приказы, доверенности, карточку предприятия'
          }
        >
          <IconFileStack size={16} /> Загрузить документы
        </Button>
      </Header>

      {expanded &&
        (isLoading ? (
          <Loader />
        ) : items.length === 0 ? (
          <Empty>
            Уставные документы не загружены. Догрузите устав, приказы о назначении
            руководителя, доверенности и карточку предприятия — они пригодятся при
            проверке полномочий подписанта.
          </Empty>
        ) : (
          <div id={`cp-docs-${counterpartyId}`}>
            <TableScroll>
              <Table>
                <thead>
                  <tr>
                    <th style={{ width: '30%' }}>Имя файла</th>
                    <th style={{ width: '18%' }}>Тип</th>
                    <th style={{ width: '10%' }}>Размер</th>
                    <th style={{ width: '12%' }}>Текст</th>
                    <th style={{ width: '16%' }}>Загружен</th>
                    <th style={{ width: '14%', textAlign: 'right' }}>Файл</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((f) => {
                    const chars = f.content_chars ?? 0;
                    const expired = f.valid_until ? isExpired(f.valid_until) : false;
                    return (
                      <FileRow
                        key={f.id}
                        id={`cp-doc-row-${f.id}`}
                        $highlight={highlightId === f.id}
                        onClick={() => onDownload(f.id, f.filename)}
                        tabIndex={0}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            onDownload(f.id, f.filename);
                          }
                        }}
                        title="Скачать файл"
                      >
                        <td>{f.filename}</td>
                        <TypeCell>
                          <Badge>{counterpartyDocTypeLabel(f.doc_type)}</Badge>
                          {f.valid_until &&
                            (expired ? (
                              <ValidExpired title="Срок действия истёк">
                                истекла {formatDate(f.valid_until)}
                              </ValidExpired>
                            ) : (
                              <ValidHint>до {formatDate(f.valid_until)}</ValidHint>
                            ))}
                        </TypeCell>
                        <td>{humanSize(f.size_bytes)}</td>
                        <MutedCell
                          title={
                            chars === 0
                              ? 'Скан без текстового слоя — извлечь текст не удалось'
                              : `${chars.toLocaleString('ru-RU')} символов текста извлечено`
                          }
                        >
                          {chars === 0
                            ? 'скан'
                            : `${chars.toLocaleString('ru-RU')} симв.`}
                        </MutedCell>
                        <td>
                          {formatDateTime(f.created_at)}
                          {f.uploaded_by && (
                            <Uploader title={f.uploaded_by}>{f.uploaded_by}</Uploader>
                          )}
                        </td>
                        <td style={{ textAlign: 'right' }}>
                          <RowActions>
                            <DownloadBtn
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                onDownload(f.id, f.filename);
                              }}
                              aria-label={`Скачать «${f.filename}»`}
                              title={`Скачать «${f.filename}»`}
                            >
                              <IconDownload size={14} />
                              Скачать
                            </DownloadBtn>
                            {can_write && (
                              <DeleteBtn
                                type="button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  onDelete(f.id, f.filename);
                                }}
                                disabled={isDeleting}
                                aria-label={`Удалить «${f.filename}»`}
                                title={`Удалить «${f.filename}»`}
                              >
                                <IconTrash size={14} />
                                Удалить
                              </DeleteBtn>
                            )}
                          </RowActions>
                        </td>
                      </FileRow>
                    );
                  })}
                </tbody>
              </Table>
            </TableScroll>
          </div>
        ))}

      {success && <SuccessBanner role="status">{success}</SuccessBanner>}

      <Modal open={modalOpen} title="Догрузить документы контрагента" onClose={onCloseModal}>
        <form onSubmit={onSubmit}>
          <Field>
            <Label htmlFor={`cp-doc-type-${counterpartyId}`}>Тип документа</Label>
            <Select
              id={`cp-doc-type-${counterpartyId}`}
              value={docType}
              // cast безопасен: <option value> заполняются только значениями
              // из COUNTERPARTY_DOC_TYPE_ORDER (того же union), других вариантов
              // у нативного select нет.
              onChange={(e) => setDocType(e.target.value as CounterpartyDocType)}
            >
              {COUNTERPARTY_DOC_TYPE_ORDER.map((t) => (
                <option key={t} value={t}>
                  {COUNTERPARTY_DOC_TYPE_LABELS[t]}
                </option>
              ))}
            </Select>
          </Field>

          {showValidUntil && (
            <Field>
              <Label htmlFor={`cp-doc-valid-${counterpartyId}`}>
                Действует до (необязательно)
              </Label>
              <Input
                id={`cp-doc-valid-${counterpartyId}`}
                type="date"
                value={validUntil}
                onChange={(e) => setValidUntil(e.target.value)}
              />
              <FieldHint>
                Срок действия доверенности. Просроченные документы подсветятся в
                списке.
              </FieldHint>
            </Field>
          )}

          <FileDropZone
            multiple
            accept={ACCEPT_LEGAL_DOC}
            hint="Устав, приказы, доверенности, карточка предприятия — можно несколько файлов сразу"
            onFiles={(files) =>
              setPicked((prev) => {
                const seen = new Set(prev.map((f) => `${f.name}::${f.size}`));
                const next = [...prev];
                for (const f of files) {
                  const key = `${f.name}::${f.size}`;
                  if (!seen.has(key)) {
                    next.push(f);
                    seen.add(key);
                  }
                }
                return next;
              })
            }
          />
          <MultiFileList
            files={picked}
            onRemove={(idx) => setPicked((prev) => prev.filter((_, i) => i !== idx))}
          />

          {error && <ErrorBanner role="alert">{error}</ErrorBanner>}

          <ModalActions>
            <Button type="button" $variant="ghost" onClick={onCloseModal}>
              Отмена
            </Button>
            <Button
              type="submit"
              disabled={isUploading || picked.length === 0 || !can_write}
              title={!can_write ? 'Только чтение' : undefined}
            >
              {isUploading ? 'Загрузка…' : `Загрузить ${picked.length || ''}`.trim()}
            </Button>
          </ModalActions>
        </form>
      </Modal>
    </Section>
  );
}
